import { Component, Host, h, Element, Prop, Event, EventEmitter, Method, Watch } from "@stencil/core";

import {
  StzhToastOpenEvent,
  StzhToastOpenedEvent,
  StzhToastCloseEvent,
  StzhToastClosedEvent,
  StzhToastType,
} from "../../index";

import { StzhToastLocalizedText } from "./stzh-toast.localization";
import { hasSlot, setPropsIfNull } from "../../utils/utils";
import { waitForEvent } from "../../utils/event-utils";
import { animateTo, shimKeyframesAutoValues, stopAnimations } from "../../utils/animation-utils";

const ANIMATION_SHOW = {
  keyframes: [
    { transform: "translateY(100%)", opacity: "0", height: "0", marginBottom: "0" },
    { transform: "translateY(0px)", opacity: "1", height: "auto", marginBottom: "auto" },
  ],
  options: {
    duration: 300,
    easing: "ease-out",
  },
};

const ANIMATION_HIDE = {
  keyframes: [
    { transform: "translateX(0px)", opacity: "1", height: "auto", marginBottom: "auto" },
    { transform: "translateX(-100%)", opacity: "0", height: "auto", marginBottom: "auto" },
    { transform: "translateX(-100%)", opacity: "0", height: "0", marginBottom: "0" },
    // { transform: "translateY(0px)", opacity: "1", height: "auto", marginBottom: 'auto' },
    // { transform: "translateY(-100%)", opacity: "0", height: "0", marginBottom: '0' },
  ],
  options: {
    duration: 300,
    easing: "ease-in",
  },
};

/**
 * @slot link - Slot for optional stzh-link element
 */
@Component({
  tag: "stzh-toast",
  styleUrl: "stzh-toast.scss",
  shadow: true,
})
export class StzhToast {
  /** Label */
  @Prop() label: string = "";

  /** Type */
  @Prop({ reflect: true }) type: StzhToastType = "info";

  /** Whether to hide close button */
  @Prop({ reflect: true }) hideClose: boolean = false;

  /** Open state */
  @Prop({ reflect: true }) open: boolean = true;

  /** Whether open animation should be played initially. */
  @Prop() initialOpenAnimation = false;

  /** Translation strings. */
  @Prop() localization: StzhToastLocalizedText;

  /** Toast open event */
  @Event() stzhOpen: EventEmitter<StzhToastOpenEvent>;

  /** Toast opened event (after animation)  */
  @Event() stzhOpened: EventEmitter<StzhToastOpenedEvent>;

  /** Toast close event */
  @Event() stzhClose: EventEmitter<StzhToastCloseEvent>;

  /** Toast closed event (after animation)  */
  @Event() stzhClosed: EventEmitter<StzhToastClosedEvent>;

  @Element() element: HTMLStzhToastElement;

  @Watch("open")
  async openWatcher(newValue: boolean) {
    if (!this.rootElement) {
      return;
    }

    if (newValue) {
      if (this.toggledByMethod) {
        this.stzhOpen.emit({
          component: "stzh-toast",
        });
      }

      await stopAnimations(this.element);

      // this.element.style.willChange = "transform, opacity, height";
      this.element.hidden = false;

      const computedStyle = getComputedStyle(this.element, null);

      await animateTo(
        this.element,
        shimKeyframesAutoValues(ANIMATION_SHOW.keyframes, {
          height: this.element.scrollHeight,
          marginTop: parseInt(computedStyle["margin-top"]),
          marginBottom: parseInt(computedStyle["margin-bottom"]),
        }),
        ANIMATION_SHOW.options,
      );

      this.element.style.height = "auto";
      this.element.style.opacity = "1";
      // this.element.style.willChange = "auto";

      if (this.toggledByMethod) {
        this.stzhOpened.emit({
          component: "stzh-toast",
        });
      }
    } else {
      if (this.toggledByMethod) {
        this.stzhClose.emit({
          component: "stzh-toast",
        });
      }

      await stopAnimations(this.element);

      // this.element.style.willChange = "transform, opacity, height";

      const computedStyle = getComputedStyle(this.element, null);

      await animateTo(
        this.element,
        shimKeyframesAutoValues(ANIMATION_HIDE.keyframes, {
          height: this.element.scrollHeight,
          marginTop: parseInt(computedStyle["margin-top"]),
          marginBottom: parseInt(computedStyle["margin-bottom"]),
        }),
        ANIMATION_HIDE.options,
      );

      this.element.style.height = "0px";
      this.element.style.opacity = "0";
      this.element.hidden = true;
      // this.element.style.willChange = "auto";

      if (this.toggledByMethod) {
        this.stzhClosed.emit({
          component: "stzh-toast",
        });
      }
    }

    this.toggledByMethod = false;
  }

  @Method()
  async show() {
    if (this.open) {
      return;
    }

    this.toggledByMethod = true;
    this.open = true;
    return waitForEvent(this.element, "stzhOpened");
  }

  @Method()
  async hide() {
    if (!this.open) {
      return;
    }

    this.toggledByMethod = true;
    this.open = false;
    return waitForEvent(this.element, "stzhClosed");
  }

  // private observer: MutationObserver;
  private toggledByMethod: boolean;
  private rootElement: HTMLElement;

  private handleLinkSlotchange = () => {
    const host = (this.element.getRootNode() as ShadowRoot)?.host || this.element;
    const buttons = host.querySelectorAll('stzh-button[slot="link"], [slot="link"] stzh-button');

    buttons.forEach(link => {
      setPropsIfNull(link, {
        size: "small",
        variant: "tertiary",
      } as HTMLStzhButtonElement);
    });
  };

  private onCloseButtonClick = () => {
    this.hide();
  };

  async componentWillLoad() {
    if (!this.localization) {
      this.localization = await window.stzhComponents.utils.fetchTranslations(this.element, "toast");
    }
  }

  componentDidLoad() {
    if (this.initialOpenAnimation && this.open) {
      // hide first so the open animation will be shown
      this.element.hidden = true;
      this.element.style.height = "0px";
      this.element.style.opacity = "0";

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.openWatcher(true);
        });
      });
    } else {
      this.element.hidden = !this.open;
      this.element.style.height = this.open ? "auto" : "0px";
      this.element.style.opacity = this.open ? "1" : "0";
    }
  }

  // connectedCallback() {
  //   this.init();

  //   this.observer = new MutationObserver(this.init);
  //   this.observer.observe(this.element, {
  //     childList: true,
  //     subtree: true
  //   });
  // }

  // disconnectedCallback() {
  //   if (this.observer) {
  //     this.observer.disconnect();
  //   }
  // }

  render() {
    const linkUsed = hasSlot(this.element, "link");

    const classes = {
      "stzh-toast": true,
      "stzh-toast--has-link": linkUsed,
      "stzh-toast--has-close-hidden": this.hideClose,
      [`stzh-toast--type-${this.type}`]: !!this.type,
    };

    return (
      <Host>
        <div ref={el => (this.rootElement = el as HTMLElement)} class={classes}>
          <div class="stzh-toast__content">
            <div class="stzh-toast__icon-wrapper">
              <stzh-icon
                class="stzh-toast__icon"
                name={
                  (this.type === "info" && "info-help-filled") ||
                  (this.type === "success" && "checkmark-circle-filled") ||
                  (this.type === "warning" && "important-warning-filled") ||
                  (this.type === "error" && "warning-filled")
                }
              ></stzh-icon>
            </div>
            <div class="stzh-toast__text-wrapper">
              <div class="stzh-toast__label">{this.label}</div>
              <div class="stzh-toast__link">
                <slot name="link" onSlotchange={this.handleLinkSlotchange}></slot>
              </div>
            </div>
          </div>

          {!this.hideClose && (
            <div class="stzh-toast__close-wrapper">
              <stzh-button
                class="stzh-toast__close"
                onClick={this.onCloseButtonClick}
                label={this.localization.close}
                variant="tertiary"
                size="small"
                iconOnly={true}
                icon="close-big"
              ></stzh-button>
            </div>
          )}
        </div>
      </Host>
    );
  }
}
